/**
 * Browser viewport presets for agent browser sessions.
 *
 * Organizations pick a default preset (Settings → Agent Browser) and individual
 * Workflows may save a preference, and individual runs may override it
 * (Flow Designer → Run with config). The stored value is
 * the preset *key*; `resolveBrowserViewport` maps it to concrete dimensions
 * before reaching `PlaywrightClient.connect({ viewport })`.
 *
 * The `laptop` default (1440×900) matches `DEFAULT_VIEWPORT` in
 * `@chatsdet/browser-core`. It replaced `desktop` (1280×800, the resolution
 * usually quoted for computer-use models) on 2026-09-14: apps that dock a side
 * panel — our own Flow Designer with the Assistant drawer open — left agents
 * ~1000 px of working canvas at 1280 and they misread it as clipped.
 *
 * @module browser-viewport
 */

/** Device family for a preset — drives the icon shown in the visual picker. */
export type BrowserViewportDevice = 'desktop' | 'laptop' | 'tablet' | 'mobile';

export interface BrowserViewportPresetSpec {
  /** Human-friendly label shown in the UI. */
  label: string;
  width: number;
  height: number;
  device: BrowserViewportDevice;
}

export const BROWSER_VIEWPORT_PRESETS = {
  desktop: { label: 'Desktop', width: 1280, height: 800, device: 'desktop' },
  desktop_large: { label: 'Large desktop', width: 1920, height: 1080, device: 'desktop' },
  laptop: { label: 'Laptop', width: 1440, height: 900, device: 'laptop' },
  tablet: { label: 'Tablet', width: 768, height: 1024, device: 'tablet' },
  mobile: { label: 'Mobile', width: 390, height: 844, device: 'mobile' },
} as const satisfies Record<string, BrowserViewportPresetSpec>;

export type BrowserViewportPreset = keyof typeof BROWSER_VIEWPORT_PRESETS;

export const DEFAULT_BROWSER_VIEWPORT_PRESET: BrowserViewportPreset = 'laptop';

export const BROWSER_VIEWPORT_PRESET_KEYS = Object.keys(
  BROWSER_VIEWPORT_PRESETS
) as BrowserViewportPreset[];

/** Type guard: is `value` a known preset key? */
export function isBrowserViewportPreset(value: unknown): value is BrowserViewportPreset {
  return typeof value === 'string' && Object.hasOwn(BROWSER_VIEWPORT_PRESETS, value);
}

/** Map a canonical preset onto the deprecated desktop/mobile API contract. */
export function toLegacyDeviceViewport(preset: BrowserViewportPreset): 'desktop' | 'mobile' {
  return preset === 'mobile' ? 'mobile' : 'desktop';
}

/**
 * Map a stored preset key (possibly null/unknown) to concrete viewport
 * dimensions, falling back to the laptop default.
 */
export function resolveBrowserViewport(preset: string | null | undefined): {
  width: number;
  height: number;
} {
  const spec = isBrowserViewportPreset(preset)
    ? BROWSER_VIEWPORT_PRESETS[preset]
    : BROWSER_VIEWPORT_PRESETS[DEFAULT_BROWSER_VIEWPORT_PRESET];
  return { width: spec.width, height: spec.height };
}
